import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Skeleton } from "../../ui/skeleton"
import { MessageSquareText, Megaphone, Code2, Mail, CheckCircle } from 'lucide-react'

export function UseCases() {
  const [isLoading, setIsLoading] = useState(true)
  
  const useCases = [
    { icon: MessageSquareText, title: "Coaching", color: "from-blue-500 to-cyan-500", items: ["Session summaries", "Jump starters", "Framework fetch"] },
    { icon: Megaphone, title: "Marketing", color: "from-purple-500 to-pink-500", items: ["Scripts", "Checklists", "Articles"] },
    { icon: Code2, title: "Tech", color: "from-green-500 to-teal-500", items: ["Test cases", "PRDs", "Code generation"] },
    { icon: Mail, title: "Content", color: "from-orange-500 to-red-500", items: ["Emails", "Messages", "Follow-ups"] },
  ]

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 1200)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section id="use-cases" className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-br from-slate-50/50 to-blue-50/50 dark:from-slate-900 dark:to-slate-800">
      <div className="absolute inset-0 opacity-40 dark:opacity-20">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><g fill="#e2e8f0" fill-opacity="0.05"><polygon points="50 0 60 40 100 50 60 60 50 100 40 60 0 50 40 40"/></g></svg>')}')`,
            backgroundSize: '100px 100px'
          }}
        />
      </div>
      <div className="container mx-auto max-w-[1200px] px-10 md:px-[72px] relative z-10">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            What <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">AIRA</span> Can Do
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From coaching sessions to code generation, AIRA adapts to your specific needs across every department.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {isLoading ? (
            // Skeleton loading state
            Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="group relative bg-background/70 backdrop-blur-xl border-border/50 overflow-hidden h-full">
                <Skeleton className="absolute top-0 left-0 right-0 h-1" />
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <Skeleton className="w-10 h-10 rounded-lg" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pb-6">
                  <div className="flex items-center space-x-3">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="flex items-center space-x-3">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex items-center space-x-3">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            useCases.map((useCase, index) => {
              const Icon = useCase.icon
              return (
                <Card key={index} className="group relative bg-background/90 backdrop-blur-xl border-[1px] border-border/80 hover:scale-105 transition-all duration-500 shadow-lg -translate-y-1 overflow-hidden h-full">
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${useCase.color} opacity-100 transition-opacity duration-300`}></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${useCase.color} p-2 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="h-full w-full text-white" />
                      </div>
                      <CardTitle className="text-xl text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors duration-300">
                        {useCase.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 pb-6">
                    {useCase.items.map((item, itemIndex) => (
                      <div 
                        key={itemIndex} 
                        className="flex items-center space-x-3 group/item hover:translate-x-1 transition-transform duration-200"
                        style={{ 
                          animation: `slideInLeft 0.5s ease-out ${itemIndex * 0.1}s both`,
                          opacity: 0
                        }}
                      >
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-foreground group-hover/item:text-indigo-600 dark:group-hover/item:text-indigo-400 transition-colors duration-200">
                          {item}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                  <div className={`absolute inset-0 group-hover:bg-gradient-to-r ${useCase.color} opacity-5 transition-opacity duration-300 rounded-lg`}></div>
                </Card>
              )
            })
          )}
        </div>
      </div>
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </section>
  )
}


